import { useEffect, useState } from 'react';
import { FilterGroup, FilterLabel } from '../../../../../../components/Filters';
import { searchProdutosCatalogo } from '../../filterServices';
import { MonitoramentoPeriodo, MonitoramentoTipo } from '../../types';
import { type DetalhamentoMonitoramentoValue, type TipoComparacaoMonitoramento } from '../DetalhamentoMonitoramentoForm';
import styles from './FiltrosResumo.module.scss';

const PERIODO_LABELS: Record<MonitoramentoPeriodo, string> = {
  [MonitoramentoPeriodo.ULTIMAS_4_SEMANAS]: 'Últimas 4 semanas',
  [MonitoramentoPeriodo.ULTIMAS_8_SEMANAS]: 'Últimas 8 semanas',
  [MonitoramentoPeriodo.ULTIMAS_12_SEMANAS]: 'Últimas 12 semanas',
};

const formatLocal = (detalhamento: DetalhamentoMonitoramentoValue): string => {
  if (detalhamento.localTipo === 'TODO_TERRITORIO') return 'Todo o território';
  if (detalhamento.localTipo === 'UF') return `UF selecionadas (${detalhamento.ufs.length})`;
  return `Cidades selecionadas (${detalhamento.cidades.length})`;
};

const Chip = ({ label }: { label: string }) => <span className={styles.chip}>{label}</span>;

type FiltrosResumoProps = {
  monitoringType: MonitoramentoTipo;
  detalhamento: DetalhamentoMonitoramentoValue;
};

export const FiltrosResumo = ({ monitoringType, detalhamento }: FiltrosResumoProps) => {
  const isVarejo = monitoringType === MonitoramentoTipo.COMPETITIVIDADE_VAREJO;

  // Na Análise o produto de referência só tem o GTIN, sem nome — resolve o nome pra exibição via
  // catálogo (a criação em si usa só o GTIN, então essa busca é só cosmética, não bloqueia o submit).
  const produtoReferenciaEan = !isVarejo ? (detalhamento.produtos[0]?.produtoEan ?? null) : null;
  const [produtoReferenciaNome, setProdutoReferenciaNome] = useState<string | null>(null);

  useEffect(() => {
    if (!produtoReferenciaEan) {
      setProdutoReferenciaNome(null);
      return undefined;
    }

    let isCurrent = true;
    searchProdutosCatalogo(produtoReferenciaEan).then(options => {
      if (!isCurrent) return;
      setProdutoReferenciaNome(options.find(option => option.produtoEan === produtoReferenciaEan)?.produtoNome ?? null);
    });

    return () => {
      isCurrent = false;
    };
  }, [produtoReferenciaEan]);

  const monitorarLabel = isVarejo ? (detalhamento.redeReferencia?.redeNome ?? '—') : (produtoReferenciaNome ?? produtoReferenciaEan ?? '—');
  const monitorarVersusLabel = isVarejo
    ? `Redes concorrentes (${detalhamento.redesConcorrentes.length})`
    : `Produtos competidores (${Math.max(detalhamento.produtos.length - 1, 0)})`;

  // No modo varejo o "grupo" reflete o escopo de comparação ativo (Produto/Categoria/Seção) — sem
  // isso, um monitoramento pré-preenchido em escopo Categoria/Seção mostraria "Produtos selecionados
  // (0)", que é enganoso mesmo o submit desse escopo estando bloqueado por enquanto.
  const varejoGrupoPorTipo: Record<TipoComparacaoMonitoramento, { label: string; valor: string }> = {
    PRODUTO: { label: 'PRODUTOS', valor: `Produtos selecionados (${detalhamento.produtos.length})` },
    CATEGORIA: { label: 'CATEGORIAS', valor: `Categorias selecionadas (${detalhamento.categorias.length})` },
    SECAO: { label: 'SEÇÕES', valor: `Seções selecionadas (${detalhamento.secoes.length})` },
  };
  const grupoLabel = isVarejo ? varejoGrupoPorTipo[detalhamento.tipoComparacao].label : 'REDES';
  const grupoValor = isVarejo
    ? varejoGrupoPorTipo[detalhamento.tipoComparacao].valor
    : `Redes selecionadas (${detalhamento.redesConcorrentes.length + (detalhamento.redeReferencia ? 1 : 0)})`;

  return (
    <div className={styles.container}>
      <FilterGroup className={styles.row}>
        <FilterLabel>Monitorar</FilterLabel>
        <Chip label={monitorarLabel} />
        <span className={styles.versus}>Vs</span>
        <Chip label={monitorarVersusLabel} />
      </FilterGroup>

      <FilterGroup className={styles.row}>
        <FilterLabel>{grupoLabel}</FilterLabel>
        <Chip label={grupoValor} />
      </FilterGroup>

      <FilterGroup className={styles.row}>
        <FilterLabel>Local</FilterLabel>
        <Chip label={formatLocal(detalhamento)} />
      </FilterGroup>

      <FilterGroup className={styles.row}>
        <FilterLabel>Período</FilterLabel>
        <Chip label={PERIODO_LABELS[detalhamento.periodo]} />
      </FilterGroup>
    </div>
  );
};
